import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import Table from '@lmc/ui-kit/src/molecules/Table/Table'
import { Device, DeviceStatus, Home } from '@lmc/ts-common'
import DotsButton from 'screens/Residents/screens/ResidentsGoalsScreen/components/DotsMenu/DotsMenu'
import dayjs from 'dayjs'
import ResidentCell from '@lmc/ui-kit/src/molecules/Table/cells/ResidentCell'
import { ConfirmationModal, Dropdown } from '@lmc/ui-kit'
import { Menu, TablePaginationConfig } from 'antd'
import { useUpdateDeviceById } from '../../hooks'
import { useSelector } from 'react-redux'
import { generalConstants } from 'locale/general'
import useGroupAccess from 'components/AccessControl/useGroupAccess'
import { Text } from '@lmc/ui-kit/src/general'
import styled, { css } from 'styled-components'
import { TableParams } from '../../types'
import useCarers, { getCarer } from 'hooks/useFindItem/useCarers'
import { ColumnsType } from 'antd/es/table'

type DevicesTableProps = {
  devices: Device[]
  tableParams: TableParams
  handleTableChange: (pagination: TablePaginationConfig) => void
  hasDeviceAccessControl?: boolean
  navigate: (route: string) => void
  refetch: () => void
  testId?: string
}

const DevicesTable: FC<DevicesTableProps> = ({
  devices,
  tableParams,
  handleTableChange,
  hasDeviceAccessControl,
  navigate,
  refetch,
  testId
}) => {
  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const hasGroupAccess = useGroupAccess()

  const home: string =
    useSelector((state: { global: { home: Home } }) => state.global.home.id) ||
    ''

  const { updateDevice, isLoading: isLoadingUpdateDevice } =
    useUpdateDeviceById(() => refetch())

  const rows = devices?.map((device, index) => ({
    deviceName: device.name,
    deviceModel: device.model,
    lastSeen: dayjs(device.lastUsedOn).format('HH:mm DD/MM/YYYY '),
    lastUsedBy: device.lastUsedBy.id,
    action: {
      id: device.id,
      index: index,
      name: device.name,
      status: (device as unknown as { status: DeviceStatus })?.status
    },
    key: device.id,
    appVersion: device?.version || 'N/A'
  }))

  const { all: carers } = useCarers({})

  const handleChangeStatus = (status: DeviceStatus, home: string, id: string) =>
    updateDevice({
      id,
      homes: [{ status, home }]
    })

  const hasPagination: object | false =
    tableParams?.pagination?.total && tableParams?.pagination?.total > 0
      ? tableParams.pagination
      : false

  const columns: ColumnsType<(typeof rows)[0]> = [
    {
      dataIndex: 'deviceName',
      title: t(dacConstants.DEVICE_NAME)
    },
    {
      dataIndex: 'deviceModel',
      title: t(dacConstants.DEVICE_MODEL)
    },
    {
      dataIndex: 'appVersion',
      title: t(dacConstants.APP_VERSION)
    },
    {
      dataIndex: 'lastSeen',
      title: t(dacConstants.LAST_SEEN)
    },
    {
      dataIndex: 'lastUsedBy',
      title: t(dacConstants.LAST_USED_BY),
      render: (id: string) => {
        const user = getCarer(carers, id)
        return (
          user && (
            <ResidentCell
              avatarSize={30}
              resident={{
                name: user.name,
                picture: user?.picture as unknown as string
              }}
            />
          )
        )
      }
    },

    {
      dataIndex: 'action',
      title: ' ',
      render: ({ id, index, name, status }) => (
        <div
          onClick={(e) => e.stopPropagation()}
          data-testid={testId ? `${testId}-actions-${index}` : undefined}
        >
          <Dropdown
            overlay={
              <Menu
                data-testid={
                  testId ? `${testId}-actions-menu-${index}` : undefined
                }
              >
                {(status === DeviceStatus.blocked ||
                  status === DeviceStatus.pending) && (
                  <ConfirmationModal
                    localisation={{
                      confirmText: t(generalConstants.CONFIRM),
                      title: t(dacConstants.APPROVE_DEVICE),
                      cancelText: t(generalConstants.CANCEL)
                    }}
                    modalContent={
                      <ModalContentWrapper>
                        <Text level={1}>
                          {t(
                            dacConstants[
                              hasGroupAccess
                                ? 'APPROVE_DEVICE_GROUP_DESC'
                                : 'APPROVE_DEVICE_NOT_GROUP_DESC'
                            ]
                          )}
                        </Text>
                        <StyledText level={1}>
                          {t(
                            dacConstants.ARE_YOU_SURE_YOU_WANT_APPROVE_THIS_SITE
                          )}
                        </StyledText>
                      </ModalContentWrapper>
                    }
                    confirmButtonProps={{ variant: 'primary' }}
                  >
                    <Menu.Item
                      data-cy="btn-approve"
                      key="1"
                      onClick={() =>
                        handleChangeStatus(DeviceStatus.approved, home, id)
                      }
                      disabled={!hasDeviceAccessControl}
                      data-testid={
                        testId ? `${testId}-approve-${index}` : undefined
                      }
                    >
                      {t(dacConstants.APPROVE)}
                    </Menu.Item>
                  </ConfirmationModal>
                )}
                {(status === DeviceStatus.approved ||
                  status === DeviceStatus.pending) && (
                  <ConfirmationModal
                    localisation={{
                      confirmText: t(generalConstants.CONFIRM),
                      title: t(dacConstants.BLOCK_DEVICE),
                      cancelText: t(generalConstants.CANCEL)
                    }}
                    modalContent={
                      <ModalContentWrapper>
                        <Text level={1}>
                          {t(
                            dacConstants[
                              hasGroupAccess
                                ? 'BLOCK_DEVICE_GROUP_DESC'
                                : 'BLOCK_DEVICE_NOT_GROUP_DESC'
                            ]
                          )}
                        </Text>
                        <StyledText level={1}>
                          {t(
                            dacConstants.ARE_YOU_SURE_YOU_WANT_BLOCK_THIS_SITE
                          )}
                        </StyledText>
                      </ModalContentWrapper>
                    }
                  >
                    <Menu.Item
                      data-cy="btn-block"
                      key="0"
                      onClick={() =>
                        handleChangeStatus(DeviceStatus.blocked, home, id)
                      }
                      disabled={!hasDeviceAccessControl}
                      data-testid={
                        testId ? `${testId}-block-${index}` : undefined
                      }
                    >
                      {t(dacConstants.BLOCK)}
                    </Menu.Item>
                  </ConfirmationModal>
                )}
                <Menu.Item
                  data-cy="btn-manage"
                  key="3"
                  onClick={() => navigate(`/organisation/devices/${id}/manage`)}
                  data-testid={testId ? `${testId}-manage-${index}` : undefined}
                >
                  {t(dacConstants.MANAGE)}
                </Menu.Item>
                <Menu.Item
                  data-cy="btn-view-device-sign"
                  disabled={!hasDeviceAccessControl}
                  key="4"
                  onClick={() =>
                    navigate(`/organisation/devices/signs/?deviceId=${id}`)
                  }
                  data-testid={
                    testId ? `${testId}-view-device-${index}` : undefined
                  }
                >
                  {t(dacConstants.VIEW_DEVICE)}
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
            placement="bottomRight"
          >
            <DotsButton
              data-testid={
                testId ? `${testId}-dots-button-${index}` : undefined
              }
            />
          </Dropdown>
        </div>
      )
    }
  ]

  return (
    <TableStyled<(typeof rows)[0]>
      onRow={(record: { key: string }) => ({
        onClick: () => navigate(`${record.key}/manage`)
      })}
      onChange={handleTableChange}
      pagination={hasPagination}
      loading={isLoadingUpdateDevice}
      columns={columns}
      dataSource={rows}
      data-testid={testId}
    />
  )
}

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledText = styled(Text)`
  font-weight: 500;
`

const cssTable = css`
  .ant-table-row {
    cursor: pointer;
  }
`

const TableStyled = styled(Table)`
  ${cssTable}
` as typeof Table

export default DevicesTable
